import axios from 'axios';
import { advertizerApiHost } from '../../constants/envs';
import { ITelegramAccountRepository, TelegramAccountListResult } from '../../application/ports/ITelegramAccountRepository';
import { TelegramAccount } from '../../shared/types/TelegramAccount';
import { TelegramAccountEntity } from '../../domain/entities/TelegramAccount';
import { mapApiToEntity } from '../mappers/telegramAccounts.mapper'
import { mapApiToEntity as mapBrandingApiToEntity } from '../mappers/telegramAccountBranding.mapper'
import { TelegramAccountBrandingEntity } from '../../domain/entities/TelegramAccountBranding'
import { UpdateTelegramAccountBrandingInput } from '../../shared/types/TelegramAccountBranding'
import { AuthTokenProvider } from '../auth/AuthTokenProvider';

export class TelegramAccountsAxiosRepository implements ITelegramAccountRepository {
  constructor(private auth: AuthTokenProvider) {}

  private async authHeaders() {
    const token = await this.auth.getToken();
    if (!token) throw new Error('Unauthenticated');
    return { Authorization: `Bearer ${token}` };
  }

  async list(params: { page: number; perPage: number; search?: string }): Promise<TelegramAccountListResult> {
    const headers = await this.authHeaders();
    const res = await axios.get(advertizerApiHost + '/api/telegram_accounts', { params: { page: params.page, perPage: params.perPage, search: params.search }, headers });
    return { items: (res.data.telegramAccounts || []).map(mapApiToEntity), page: res.data.pagination.page, perPage: res.data.pagination.perPage, total: res.data.pagination.total };
  }

  async get(uuid: string): Promise<TelegramAccountEntity | null> {
    const headers = await this.authHeaders();
    const res = await axios.get(advertizerApiHost + '/api/telegram_accounts/' + uuid, { headers });
    return res.data ? mapApiToEntity(res.data) : null;
  }

  async create(input: { phone: string }): Promise<TelegramAccountEntity> {
    const headers = await this.authHeaders();
    const res = await axios.post(advertizerApiHost + '/api/telegram_accounts', input, { headers });
    return mapApiToEntity(res.data);
  }

  async update(uuid: string, input: Partial<Pick<TelegramAccount, 'status'>>): Promise<TelegramAccountEntity> {
    const headers = await this.authHeaders();
    const res = await axios.put(advertizerApiHost + '/api/telegram_accounts/' + uuid, input, { headers });
    return mapApiToEntity(res.data);
  }

  async delete(uuid: string): Promise<void> {
    const headers = await this.authHeaders();
    await axios.delete(advertizerApiHost + '/api/telegram_accounts/' + uuid, { headers });
  }

  async startAuth(uuid: string): Promise<void> {
    const headers = await this.authHeaders();
    await axios.post(advertizerApiHost + `/api/telegram_accounts/${uuid}/auth`, {}, { headers });
  }

  async finishAuth(uuid: string, input: { code: string; password: string | null}): Promise<void> {
    const headers = await this.authHeaders();
    await axios.post(advertizerApiHost + `/api/telegram_accounts/${uuid}/auth/confirm`, { confirmation_code: input.code, password: input.password }, { headers });
  }

  async getBranding(uuid: string): Promise<TelegramAccountBrandingEntity> {
    const headers = await this.authHeaders();
    const res = await axios.get(advertizerApiHost + `/api/telegram_accounts/${uuid}/branding`, { headers });
    return mapBrandingApiToEntity(res.data);
  }

  async updateBranding(uuid: string, input: UpdateTelegramAccountBrandingInput): Promise<TelegramAccountBrandingEntity> {
    const headers = await this.authHeaders();
    const fd = new FormData();
    fd.append('firstName', input.firstName);
    fd.append('lastName', input.lastName ?? '');
    fd.append('about', input.about ?? '');
    if (input.avatar) {
      fd.append('avatar', input.avatar, input.avatar.name);
    }
    const res = await axios.put(advertizerApiHost + `/api/telegram_accounts/${uuid}/branding`, fd, { headers });
    return mapBrandingApiToEntity(res.data);
  }
}
